import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../../services/api';
import { notify, confirmAsync } from '../../utils/notify';
import './PublicationSlots.css';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function PublicationSlots() {
  const { t } = useTranslation();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [queueStats, setQueueStats] = useState(null);
  const [resweeping, setResweeping] = useState(false);
  const [formData, setFormData] = useState({
    day_of_week: 0,
    hour: 9,
    minute: 0,
    max_posts_per_slot: 10,
    is_active: true,
    priority: 1,
  });

  useEffect(() => {
    fetchSlots();
    fetchQueueStats();
  }, []);

  const fetchSlots = async () => {
    try {
      const response = await apiClient.get('/api/v1/admin/publication-slots');
      setSlots(response.data);
    } catch (error) {
      console.error('Error fetching slots:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQueueStats = async () => {
    try {
      const res = await apiClient.get('/api/v1/admin/publication-queue/stats');
      setQueueStats(res.data);
    } catch (err) {
      // Non-fatal — the orphan CTA stays usable even if stats fail to load.
      console.error('Error fetching queue stats:', err);
    }
  };

  const handleRescheduleOrphans = async () => {
    setResweeping(true);
    try {
      const res = await apiClient.post(
        '/api/v1/admin/publication-slots/reschedule-orphans',
        {},
      );
      if (res.data?.dispatched) {
        notify.success(t('admin.slots.rescheduleDispatched', {
          candidate_count: res.data.candidate_count,
        }));
      } else {
        notify.success(t('admin.slots.rescheduleSuccess', {
          scheduled: res.data?.scheduled || 0,
          still_orphan: res.data?.still_orphan || 0,
        }));
      }
      fetchQueueStats();
      fetchSlots();
    } catch (err) {
      notify.error(err.response?.data?.detail || t('admin.slots.rescheduleError'));
    } finally {
      setResweeping(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingSlot) {
        await apiClient.put(`/api/v1/admin/publication-slots/${editingSlot.id}`, formData);
      } else {
        await apiClient.post('/api/v1/admin/publication-slots', formData);
      }
      fetchSlots();
      // The backend dispatches an orphan resweep on every slot write; refresh
      // the orphan counter so the admin sees the effect within a few seconds.
      setTimeout(fetchQueueStats, 3000);
      setShowModal(false);
      setEditingSlot(null);
    } catch (error) {
      notify.error(error.response?.data?.detail || 'Error saving slot');
    }
  };

  const handleDelete = async (slotId) => {
    if (!(await confirmAsync({ message: t('admin.publicationSlots.confirmDelete'), danger: true }))) return;

    try {
      await apiClient.delete(`/api/v1/admin/publication-slots/${slotId}`);
      fetchSlots();
      setTimeout(fetchQueueStats, 3000);
    } catch (error) {
      notify.error(error.response?.data?.detail || 'Error deleting slot');
    }
  };

  const openEditModal = (slot) => {
    setEditingSlot(slot);
    setFormData({
      day_of_week: slot.day_of_week,
      hour: slot.hour,
      minute: slot.minute,
      max_posts_per_slot: slot.max_posts_per_slot,
      is_active: slot.is_active,
      priority: slot.priority,
    });
    setShowModal(true);
  };

  const openAddModal = () => {
    setEditingSlot(null);
    setFormData({
      day_of_week: 0,
      hour: 9,
      minute: 0,
      max_posts_per_slot: 10,
      is_active: true,
      priority: 1,
    });
    setShowModal(true);
  };

  // Group slots by day
  const slotsByDay = DAYS.map((day, index) => ({
    day,
    dayIndex: index,
    slots: slots.filter((s) => s.day_of_week === index),
  }));

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return (
    <div className="publication-slots-page">
      <div className="page-header">
        <h1>{t('admin.publicationSlots.title')}</h1>
        <div className="page-header-actions">
          <button className="btn btn-primary" onClick={openAddModal}>
            {t('admin.publicationSlots.addSlot')}
          </button>
        </div>
      </div>

      {/* Orphan-approved posts callout: surfaces a count + manual sweep CTA.
          The backend already runs a periodic sweep every 15 minutes and
          dispatches one on every slot write; this button lets admins force
          an immediate sweep right after editing capacity. */}
      {queueStats && (queueStats.orphan_approved_count || 0) > 0 && (
        <div className="orphan-callout">
          <div className="orphan-callout-text">
            ⚠️ {t('admin.slots.orphanCount', {
              count: queueStats.orphan_approved_count,
            })}
          </div>
          <button
            className="btn btn-secondary"
            onClick={handleRescheduleOrphans}
            disabled={resweeping}
          >
            {resweeping ? '...' : t('admin.slots.rescheduleOrphans')}
          </button>
        </div>
      )}

      <div className="slots-grid">
        {slotsByDay.map(({ day, dayIndex, slots: daySlots }) => (
          <div key={dayIndex} className="day-column">
            <h3>{t(`days.${day}`)}</h3>
            <div className="day-slots">
              {daySlots.length === 0 ? (
                <div className="no-slots">{t('admin.publicationSlots.noSlots')}</div>
              ) : (
                daySlots.map((slot) => (
                  <div
                    key={slot.id}
                    className={`slot-card ${slot.is_active ? 'active' : 'inactive'}`}
                    onClick={() => openEditModal(slot)}
                  >
                    <div className="slot-time">{slot.time_str}</div>
                    <div className="slot-info">
                      <span>Max: {slot.max_posts_per_slot}</span>
                      <span>P: {slot.priority}</span>
                    </div>
                    <button
                      className="btn-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(slot.id);
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Modal — portal to body so overflow doesn't clip */}
      {showModal && ReactDOM.createPortal(
        <div className="ps-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="ps-modal" onClick={(e) => e.stopPropagation()}>
            <h2>
              {editingSlot
                ? t('admin.publicationSlots.editSlot')
                : t('admin.publicationSlots.addSlot')}
            </h2>

            <div className="form-group">
              <label>{t('admin.publicationSlots.dayOfWeek')}</label>
              <select
                value={formData.day_of_week}
                onChange={(e) =>
                  setFormData({ ...formData, day_of_week: parseInt(e.target.value) })
                }
              >
                {DAYS.map((day, i) => (
                  <option key={i} value={i}>
                    {t(`days.${day}`)}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('admin.publicationSlots.hour')}</label>
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={formData.hour}
                  onChange={(e) =>
                    setFormData({ ...formData, hour: parseInt(e.target.value) })
                  }
                />
              </div>
              <div className="form-group">
                <label>{t('admin.publicationSlots.minute')}</label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={formData.minute}
                  onChange={(e) =>
                    setFormData({ ...formData, minute: parseInt(e.target.value) })
                  }
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t('admin.publicationSlots.maxPosts')}</label>
              <input
                type="number"
                min="1"
                max="100"
                value={formData.max_posts_per_slot}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    max_posts_per_slot: parseInt(e.target.value),
                  })
                }
              />
            </div>

            <div className="form-group">
              <label>{t('admin.publicationSlots.priority')}</label>
              <input
                type="number"
                min="1"
                max="10"
                value={formData.priority}
                onChange={(e) =>
                  setFormData({ ...formData, priority: parseInt(e.target.value) })
                }
              />
            </div>

            <div className="form-group checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({ ...formData, is_active: e.target.checked })
                  }
                />
                {t('admin.publicationSlots.active')}
              </label>
            </div>

            <div className="ps-modal-actions">
              <button onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
              <button className="btn btn-primary" onClick={handleSave}>
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default PublicationSlots;
